import { supabase } from '../lib/supabase';

// Alert threshold configuration
const ALERT_THRESHOLDS = {
  revenue: {
    target: 50000, // Daily revenue target
    warning: 0.7, // 70% of target (yellow)
    critical: 0.5 // 50% of target (red)
  },
  overdueInvoices: {
    warning: 5, // 5+ overdue invoices (yellow)
    critical: 10 // 10+ overdue invoices (red)
  },
  technicianWorkload: {
    warning: 5, // 5+ active work orders per technician (yellow)
    critical: 8 // 8+ active work orders per technician (red)
  },
  invoiceAge: {
    warning: 15, // 15+ days old (yellow)
    critical: 30 // 30+ days old (red)
  }
};

// Helper to detect schema errors
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toCamelCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

export const alertService = {
  // Get daily revenue and check against target
  async checkRevenueTarget() {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const todayISO = today?.toISOString();

      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select('total')
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', todayISO);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching daily revenue:', error?.message);
        return null;
      }

      const dailyRevenue = data?.reduce((sum, order) => sum + parseFloat(order?.total || 0), 0);
      const target = ALERT_THRESHOLDS?.revenue?.target;
      const percentage = (dailyRevenue / target) * 100;

      let priority = 'success';
      let message = `Ingresos del día: $${dailyRevenue?.toFixed(2)} (${percentage?.toFixed(1)}% del objetivo)`;

      if (dailyRevenue < target * ALERT_THRESHOLDS?.revenue?.critical) {
        priority = 'critical';
        message = `⚠️ Ingresos críticos: $${dailyRevenue?.toFixed(2)} (${percentage?.toFixed(1)}% del objetivo de $${target})`;
      } else if (dailyRevenue < target * ALERT_THRESHOLDS?.revenue?.warning) {
        priority = 'warning';
        message = `⚡ Ingresos bajos: $${dailyRevenue?.toFixed(2)} (${percentage?.toFixed(1)}% del objetivo de $${target})`;
      }

      return {
        type: 'revenue',
        priority,
        message,
        value: dailyRevenue,
        target,
        percentage
      };
    } catch (error) {
      console.error('Failed to check revenue target:', error);
      throw error;
    }
  },

  // Check for overdue invoices
  async checkOverdueInvoices() {
    try {
      const { data, error } = await supabase
        ?.from('invoices')
        ?.select('id, invoice_number, client_name, total, generated_at')
        ?.in('status', ['generated', 'sent'])
        ?.order('generated_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching invoices:', error?.message);
        return null;
      }

      const now = new Date();
      const overdueInvoices = data?.filter(invoice => {
        const generatedDate = new Date(invoice?.generated_at);
        const daysDiff = Math.floor((now - generatedDate) / (1000 * 60 * 60 * 24));
        return daysDiff >= ALERT_THRESHOLDS?.invoiceAge?.warning;
      });

      const overdueCount = overdueInvoices?.length || 0;
      let priority = 'success';
      let message = `Sin facturas vencidas`;

      if (overdueCount >= ALERT_THRESHOLDS?.overdueInvoices?.critical) {
        priority = 'critical';
        message = `⚠️ ${overdueCount} facturas vencidas requieren atención urgente`;
      } else if (overdueCount >= ALERT_THRESHOLDS?.overdueInvoices?.warning) {
        priority = 'warning';
        message = `⚡ ${overdueCount} facturas vencidas pendientes de cobro`;
      } else if (overdueCount > 0) {
        priority = 'info';
        message = `${overdueCount} factura(s) vencida(s)`;
      }

      return {
        type: 'overdue_invoices',
        priority,
        message,
        count: overdueCount,
        invoices: toCamelCase(overdueInvoices || [])
      };
    } catch (error) {
      console.error('Failed to check overdue invoices:', error);
      throw error;
    }
  },

  // Check technician workload
  async checkTechnicianWorkload() {
    try {
      const { data, error } = await supabase
        ?.from('technician_profiles')
        ?.select(`
          id,
          user_id,
          current_workload,
          availability_status,
          user:user_profiles(full_name)
        `)
        ?.eq('availability_status', 'available')
        ?.order('current_workload', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching technician workload:', error?.message);
        return null;
      }

      const overloadedTechnicians = data?.filter(
        tech => tech?.current_workload >= ALERT_THRESHOLDS?.technicianWorkload?.critical
      );

      const warningTechnicians = data?.filter(
        tech => tech?.current_workload >= ALERT_THRESHOLDS?.technicianWorkload?.warning &&
                tech?.current_workload < ALERT_THRESHOLDS?.technicianWorkload?.critical
      );

      let priority = 'success';
      let message = 'Carga de trabajo balanceada';

      if (overloadedTechnicians?.length > 0) {
        priority = 'critical';
        message = `⚠️ ${overloadedTechnicians?.length} técnico(s) sobrecargado(s) (${overloadedTechnicians?.[0]?.current_workload}+ órdenes)`;
      } else if (warningTechnicians?.length > 0) {
        priority = 'warning';
        message = `⚡ ${warningTechnicians?.length} técnico(s) con alta carga de trabajo`;
      }

      return {
        type: 'technician_workload',
        priority,
        message,
        overloadedCount: overloadedTechnicians?.length || 0,
        warningCount: warningTechnicians?.length || 0,
        technicians: toCamelCase(data || [])
      };
    } catch (error) {
      console.error('Failed to check technician workload:', error);
      throw error;
    }
  },

  // Check customer feedback for low ratings and critical issues
  async checkCustomerFeedbackAlerts() {
    try {
      const CRITICAL_KEYWORDS = [
        'problema',
        'falla',
        'queja',
        'insatisfecho',
        'deficiente',
        'mal',
        'pésimo',
        'terrible',
        'inaceptable',
        'demora',
        'retraso'
      ];

      const LOW_RATING_THRESHOLD = 3;

      const { data, error } = await supabase
        ?.from('customer_feedback')
        ?.select(`
          id,
          satisfaction_rating,
          service_comments,
          submitted_at,
          work_order:work_orders(
            folio_ot,
            client:clients(
              nombre
            )
          )
        `)
        ?.lte('satisfaction_rating', LOW_RATING_THRESHOLD)
        ?.order('submitted_at', { ascending: false })
        ?.limit(5);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching customer feedback alerts:', error?.message);
        return null;
      }

      if (!data || data?.length === 0) {
        return null;
      }

      // Check for critical keywords in comments
      const criticalFeedback = data?.filter(feedback => {
        const comments = feedback?.service_comments?.toLowerCase() || '';
        return CRITICAL_KEYWORDS?.some(keyword => comments?.includes(keyword));
      });

      const lowRatingCount = data?.length;
      const criticalCount = criticalFeedback?.length;

      let priority = 'info';
      let message = '';

      if (criticalCount > 0) {
        priority = 'critical';
        message = `⚠️ ${criticalCount} comentario(s) crítico(s) detectado(s) en retroalimentación reciente`;
      } else if (lowRatingCount >= 3) {
        priority = 'warning';
        message = `⚡ ${lowRatingCount} calificaciones bajas (≤3⭐) en retroalimentación reciente`;
      } else if (lowRatingCount > 0) {
        priority = 'info';
        message = `${lowRatingCount} calificación(es) baja(s) detectada(s)`;
      }

      return {
        type: 'customer_feedback',
        priority,
        message,
        lowRatingCount,
        criticalCount,
        recentFeedback: toCamelCase(data?.slice(0, 3) || [])
      };
    } catch (error) {
      console.error('Failed to check customer feedback alerts:', error);
      throw error;
    }
  },

  // Get all alerts
  async getAllAlerts() {
    try {
      const [revenueAlert, overdueAlert, workloadAlert, feedbackAlert] = await Promise.all([
        this.checkRevenueTarget(),
        this.checkOverdueInvoices(),
        this.checkTechnicianWorkload(),
        this.checkCustomerFeedbackAlerts()
      ]);

      const alerts = [
        revenueAlert,
        overdueAlert,
        workloadAlert,
        feedbackAlert
      ]?.filter(alert => alert !== null);

      // Sort by priority: critical > warning > info > success
      const priorityOrder = { critical: 0, warning: 1, info: 2, success: 3 };
      alerts?.sort((a, b) => priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority]);

      return alerts;
    } catch (error) {
      console.error('Failed to fetch all alerts:', error);
      throw error;
    }
  },

  // Get threshold configuration (for display/customization)
  getThresholds() {
    return ALERT_THRESHOLDS;
  }
};